import { formatMcpGatewaySections, formatSubPolicyDescription, formatSubPolicySections, } from './alerts';
import { fallbackOf, riskCopy, RiskCopyMissingError, } from './risk-text/common';
import { resolveChatPattern, resolveCredentialExposure, } from './risk-text/chat';
import { resolveSecretFormat } from './risk-text/secrets';
import { resolveSecretFile } from './risk-text/secret-files';
import { resolveGatewaySecret, resolveMcpVulnerability, resolveNewMcpServer, resolveToolDescription, } from './risk-text/mcp';
import { resolveExtensionVulnerability } from './risk-text/extensions';
import { resolveBlockedPackage, resolveMaliciousPackage, resolvePii, resolveToolCall, resolveTyposquat, } from './risk-text/pii-packages-tools';
export { fillRiskTemplate } from './risk-text/common';
export { stripChatRuleDescription } from './risk-text/chat';
export { selectSecretFileVariant } from './risk-text/secret-files';
const staticRow = (key) => () => fallbackOf(riskCopy(key));
const RESOLVERS = {
    'enableDependenciesScan::vulnerability': resolveMcpVulnerability,
    // Rows 3, 7, 30: static copy; no producer today, historical records only.
    'enableDependenciesScan::unknown_package': staticRow('enableDependenciesScan::unknown_package'),
    'enableToolDescriptionsScan::permission_request': staticRow('enableToolDescriptionsScan::permission_request'),
    'maliciousPatterns::suspicious-pattern': staticRow('maliciousPatterns::suspicious-pattern'),
    'enableToolDescriptionsScan::suspicious_description': resolveToolDescription,
    'maliciousPatterns::risky-code': alert => resolveChatPattern(alert, 'risky-code'),
    'maliciousPatterns::credential-exposure': resolveCredentialExposure,
    'commandPatterns::dangerous-command': alert => resolveChatPattern(alert, 'dangerous-command'),
    'customPatterns::secret-leakage': resolveSecretFormat,
    'mcpGatewayInjection::prompt-injection': staticRow('mcpGatewayInjection::prompt-injection'),
    'secretGatewayContent::secret-leakage': resolveGatewaySecret,
    'secretFilePatterns::secret-leakage': resolveSecretFile,
    'extensionsCodeScan::extensions_code_scan': staticRow('extensionsCodeScan::extensions_code_scan'),
    'extensionsPackagesOsvDetection::osv_vulnerability': resolveExtensionVulnerability,
    'mcpServersNewServer::new_mcp_server': resolveNewMcpServer,
    'piiDetection::pii-detected': resolvePii,
    'piiDetection::multiple-pii': resolvePii,
    'piiDetection::bulk-pii': resolvePii,
    'maliciousPackagesKnownMalware::malicious_package': resolveMaliciousPackage,
    'packagesBlockedList::blocked_by_policy': resolveBlockedPackage,
    'toolCallScanMode::tool-call-scan': resolveToolCall,
    // Rows 126–137: pre-release alias keys, same copy as rows 120–125.
    'toolCallScanDetection::tool-call-scan': resolveToolCall,
    'enableToolCallScan::tool-call-scan': resolveToolCall,
    'maliciousPackagesTyposquatting::package_typosquatting': resolveTyposquat,
};
/** Security's risk-assessment sections for an alert, or null when none apply. */
export function buildRiskAssessmentSections(alert, mode = 'sections') {
    if (alert.subPolicyKey == null) {
        return null;
    }
    const resolve = RESOLVERS[`${alert.subPolicyKey}::${alert.type}`];
    return resolve == null ? null : resolve(alert, mode);
}
/** The engine's sections, or null (registry copy) if a copy key is missing. */
function riskSections(alert, mode) {
    try {
        return buildRiskAssessmentSections(alert, mode);
    }
    catch (error) {
        // A key absent from the generated table keeps the registry copy instead
        // of failing a render or v1 serialization; any other error is a bug.
        if (error instanceof RiskCopyMissingError)
            return null;
        throw error;
    }
}
function legacySections(alert) {
    return (formatMcpGatewaySections(alert.filePath ?? '', alert.type) ??
        formatSubPolicySections(alert.subPolicyKey, alert.type, alert.description, alert.location));
}
/**
 * The What/Why sections every surface renders (alert card, v1 `explanation`).
 * Flag off, or no approved copy: the legacy gateway-surface then registry copy.
 * The registry's per-finding "Detected" text is kept either way.
 */
export function formatAlertDescriptionSections(alert, options) {
    const legacy = legacySections(alert);
    const risk = options.riskAssessmentText
        ? riskSections(alert, 'sections')
        : null;
    if (risk == null) {
        return legacy;
    }
    return legacy?.detected == null
        ? risk
        : { ...risk, detected: legacy.detected };
}
/** The one-line description (dashboard widget, card subtitle). */
export function formatAlertDescriptionText(alert, options) {
    const risk = options.riskAssessmentText ? riskSections(alert, 'text') : null;
    return risk == null
        ? formatSubPolicyDescription(alert.subPolicyKey, alert.type, alert.description)
        : `${risk.whatHappened} ${risk.whyItMatters}`;
}
//# sourceMappingURL=alert-risk-text.js.map